import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { CONTACT, SOLUTION_AREAS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", organisation: "", phone: "", email: "", solution_area: "", message: "" };

export const EnquiryForm = ({ tone = "dark", source = "website", buttonLabel = "Send Enquiry" }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, source });
      setForm(EMPTY);
      navigate("/thank-you?type=enquiry");
    } catch {
      toast.error(`Could not send your enquiry. Please email ${CONTACT.sales} directly.`);
    } finally {
      setSending(false);
    }
  };

  const label = tone === "dark" ? "block text-xs tracking-[0.18em] uppercase text-slate-400 mb-2" : "block text-xs tracking-[0.18em] uppercase text-neutral-500 mb-2";
  const input = tone === "dark"
    ? "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors"
    : "w-full bg-white border border-neutral-300 text-neutral-900 placeholder:text-neutral-400 px-4 py-3 text-sm focus:outline-none focus:border-nx-gold transition-colors";

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="enquiry-form">
      <div>
        <label htmlFor={`${source}-name`} className={label}>Name *</label>
        <input id={`${source}-name`} data-testid="enquiry-name-input" required className={input} value={form.name} onChange={set("name")} placeholder="Your full name" />
      </div>
      <div>
        <label htmlFor={`${source}-org`} className={label}>Organisation Name</label>
        <input id={`${source}-org`} data-testid="enquiry-organisation-input" className={input} value={form.organisation} onChange={set("organisation")} placeholder="Company / institution" />
      </div>
      <div>
        <label htmlFor={`${source}-phone`} className={label}>Phone Number *</label>
        <input id={`${source}-phone`} data-testid="enquiry-phone-input" required type="tel" className={input} value={form.phone} onChange={set("phone")} placeholder="+91" />
      </div>
      <div>
        <label htmlFor={`${source}-email`} className={label}>Email *</label>
        <input id={`${source}-email`} data-testid="enquiry-email-input" required type="email" className={input} value={form.email} onChange={set("email")} placeholder="you@company.com" />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`${source}-area`} className={label}>Solution Area *</label>
        <select id={`${source}-area`} data-testid="enquiry-solution-area-select" required className={input} value={form.solution_area} onChange={set("solution_area")}>
          <option value="" disabled>Select a solution area</option>
          {SOLUTION_AREAS.map((a) => <option key={a} value={a} className="text-neutral-900">{a}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`${source}-msg`} className={label}>Project Requirement or Message *</label>
        <textarea id={`${source}-msg`} data-testid="enquiry-message-input" required rows={4} className={input} value={form.message} onChange={set("message")} placeholder="Tell us briefly about your site or project — our team will take it from there." />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={sending}
          data-testid="enquiry-submit-button"
          className="w-full sm:w-auto bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold tracking-wide text-sm px-10 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {sending ? "Sending…" : buttonLabel}
        </button>
      </div>
    </form>
  );
};

export const ContactAside = ({ tone = "dark" }) => {
  const line = tone === "dark" ? "border-white/10" : "border-neutral-200";
  const head = tone === "dark" ? "text-slate-400" : "text-neutral-500";
  const body = tone === "dark" ? "text-slate-100" : "text-neutral-900";
  const icon = "text-nx-goldlight";
  return (
    <div className={`flex flex-col gap-6 ${tone === "dark" ? "" : ""}`} data-testid="contact-aside">
      <div className={`pb-6 border-b ${line}`}>
        <div className={`flex items-center gap-2 text-xs tracking-[0.18em] uppercase ${head} mb-1`}><Phone size={14} className={icon} /> Customer Care</div>
        <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className={`block text-xl font-semibold ${body}`}>{CONTACT.phone}</a>
        <a href={`tel:${CONTACT.phone2.replace(/\s/g, "")}`} data-testid="contact-phone2-link" className={`block text-xl font-semibold ${body}`}>{CONTACT.phone2}</a>
        <p className={`text-xs mt-1 ${head}`}>{CONTACT.hours}</p>
      </div>
      <div className={`pb-6 border-b ${line}`}>
        <div className={`flex items-center gap-2 text-xs tracking-[0.18em] uppercase ${head} mb-1`}><Mail size={14} className={icon} /> Email</div>
        <a href={`mailto:${CONTACT.sales}`} data-testid="contact-sales-email-link" className={`block text-base font-medium ${body} hover:text-nx-goldlight transition-colors`}>{CONTACT.sales}</a>
        <a href={`mailto:${CONTACT.info}`} data-testid="contact-info-email-link" className={`block text-base font-medium ${body} hover:text-nx-goldlight transition-colors`}>{CONTACT.info}</a>
      </div>
      <div>
        <div className={`flex items-center gap-2 text-xs tracking-[0.18em] uppercase ${head} mb-1`}><MapPin size={14} className={icon} /> Office</div>
        <p className={`text-sm leading-relaxed ${body}`}>
          {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
          <span className="block mt-2">{CONTACT.hours}</span>
        </p>
      </div>
    </div>
  );
};
